import { GenericModal } from '@/components/Modal';
import { useState } from 'react';
import { alertErrorMessage, alertMessage } from '@/utils/alerts';
import { Button } from 'react-daisyui';
import { get as getApi } from '@/utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh } from '@fortawesome/free-solid-svg-icons';

export default function RefreshClientsModal({
  isOpen,
  onClose,
  updatingInProgress,
  setUpdatingInProgress,
  refreshAccountData,
}: {
  isOpen: boolean;
  onClose: () => void;
  updatingInProgress: boolean;
  setUpdatingInProgress: (boolean: boolean) => void;
  refreshAccountData: () => Promise<void>;
}) {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const handleConfirm = async () => {
    setUpdatingInProgress(true);
    let isSuccess = false;
    try {
      switch (selectedOption) {
        case 'Refresh Hubspot':
          alertMessage(
            'bottom-center',
            'Syncing in progress. Please wait a few minutes and then refresh the page.'
          );
          await getApi('clients/syncHubspot');
          await refreshAccountData();
          break;
        case 'Refresh QBO':
          alertMessage(
            'bottom-center',
            'Syncing in progress. Please wait a few minutes and then refresh the page.'
          );
          await getApi('transactions/syncQBO');
          break;
        default:
          break;
      }
      isSuccess = true;
    } catch {
      alertErrorMessage('Something went wrong. Please try again.');
    } finally {
      setTimeout(() => {
        setUpdatingInProgress(false);
        if (isSuccess) onClose();
      }, 2000);
    }
  };

  const handleIconChange = (option: string) => {
    setSelectedOption(option);
    setIsDropDownOpen(false);
  };

  const refreshOptionList = ['Refresh Hubspot', 'Refresh QBO'];

  const toggleDropdown = () => setIsDropDownOpen((prev) => !prev);

  return (
    <GenericModal isOpen={isOpen} onClose={onClose} className='max-w-[35%]'>
      <h1 className='text-lg font-bold'>Select Option</h1>
      <div className='mt-8'>
        <ul>
          <li />
          <span>
            <b>Refresh Hubspot</b> -{' '}
            {`will refresh clients from Hubspot
            including their client's list and deals`}
          </span>
          <li>
            <span>
              <b>Refresh QBO</b> - will refresh the QBO transactions for all
              clients
            </span>
          </li>
        </ul>
        <br />

        <div className='relative inline-block cursor-pointer w-[95%]'>
          <div
            role='button'
            tabIndex={0}
            id='selector'
            onKeyDown={toggleDropdown}
            className='flex align-center px-2 py-1.5 border-1 rounded input-accent border w-full'
            onClick={toggleDropdown}
          >
            <span className='mr-2'>{selectedOption || 'Select Option'}</span>
            <svg
              className={`mt-[3px] right-[6px] absolute transform ${
                isDropDownOpen ? 'rotate-180' : 'rotate-0'
              }`}
              xmlns='http://www.w3.org/2000/svg'
              viewBox='0 0 24 24'
              width='16'
              height='16'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <polyline points='6 9 12 15 18 9' />
            </svg>
          </div>
          {isDropDownOpen ? (
            <div
              role='listbox'
              className={`absolute left-0 right-0 border-1 border-t-0 border rounded-lg
                input-accent max-h-[160px] no-scrollbar overflow-y-scroll`}
            >
              {refreshOptionList.map((option) => (
                <div
                  role='option'
                  className='hover:bg-slate-800 p-2'
                  key={option}
                  aria-selected={option === selectedOption}
                  tabIndex={0}
                  onKeyDown={() => handleIconChange(option)}
                  onClick={() => handleIconChange(option)}
                >
                  <span className='relative top-[-5px]'>{option}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className='mt-10'>
          <Button
            color='accent'
            size='sm'
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={async () => handleConfirm()}
            disabled={!selectedOption || updatingInProgress}
          >
            <FontAwesomeIcon
              icon={faRefresh}
              size='lg'
              className={`mr-2 text-white ${
                updatingInProgress ? 'spinning' : ''
              }`}
            />
            Confirm
          </Button>
        </div>
      </div>
    </GenericModal>
  );
}
